import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  useToast,
  SimpleGrid,
} from '@chakra-ui/react';
import { postData } from '../../utils/httpClient.js';

export default function AddCustomerModal({ isOpen, onClose, onCustomerCreated }) {
  const [formData, setFormData] = useState({
    documentType: 2,
    documentCountry: 'UY',
    documentNumber: '',
    name: '',
    address: '',
    city: '',
    state: '',
    country: 'URUGUAY',
    email: '',
  });

  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();

  const handleChange = (field, value) => {
    // Campos que deben convertirse a mayúsculas
    const upperCaseFields = ['documentNumber', 'name', 'address', 'city', 'state'];
    
    // Convertir a mayúsculas si el campo está en la lista
    const processedValue = upperCaseFields.includes(field) ? value.toUpperCase() : value;
    
    setFormData(prev => ({
      ...prev,
      [field]: processedValue
    }));
  };

  const handleSubmit = async () => {
    // Validaciones básicas
    if (!formData.name.trim()) {
      toast({
        title: 'Error',
        description: 'El nombre es obligatorio',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!formData.documentNumber.trim()) {
      toast({
        title: 'Error',
        description: 'El número de documento es obligatorio',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsLoading(true);
      
      // Preparar datos para envío
      const customerData = {
        documentType: parseInt(formData.documentType),
        documentCountry: formData.documentCountry,
        documentNumber: formData.documentNumber.toUpperCase(),
        name: formData.name.toUpperCase(),
        address: formData.address.toUpperCase(),
        city: formData.city.toUpperCase(),
        state: formData.state.toUpperCase(),
        country: formData.country,
        email: formData.email || null,
      };

      const response = await postData('/invoices/customers', customerData);
      
      toast({
        title: 'Cliente creado exitosamente',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Resetear formulario
      setFormData({
        documentType: 2,
        documentCountry: 'UY',
        documentNumber: '',
        name: '',
        address: '',
        city: '',
        state: '',
        country: 'URUGUAY',
        email: '',
      });

      // Notificar al componente padre si hay callback
      if (onCustomerCreated) {
        onCustomerCreated(response.payload?.customer || {});
      }

      onClose();
    } catch (error) {
      toast({
        title: 'Error al crear cliente',
        description: error.message || 'No se pudo crear el cliente',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    // Resetear formulario al cerrar
    setFormData({
      documentType: 2,
      documentCountry: 'UY',
      documentNumber: '',
      name: '',
      address: '',
      city: '',
      state: '',
      country: 'URUGUAY',
      email: '',
    });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl" closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Agregar cliente factura</ModalHeader>
        <ModalCloseButton />
        
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <FormControl isRequired>
                <FormLabel>Tipo de Documento</FormLabel>
                <Select
                  value={formData.documentType}
                  onChange={(e) => handleChange('documentType', e.target.value)}
                >
                  <option value={1}>RUT</option>
                  <option value={2}>C.I.</option>
                  <option value={3}>Pasaporte</option>
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>País del Documento</FormLabel>
                <Select
                  value={formData.documentCountry}
                  onChange={(e) => handleChange('documentCountry', e.target.value)}
                >
                  <option value="UY">Uruguay</option>
                  <option value="AR">Argentina</option>
                  <option value="BR">Brasil</option>
                  <option value="US">Estados Unidos</option>
                  <option value="ES">España</option>
                </Select>
              </FormControl>
            </SimpleGrid>

            <FormControl isRequired>
              <FormLabel>Número de Documento</FormLabel>
              <Input
                value={formData.documentNumber}
                onChange={(e) => handleChange('documentNumber', e.target.value)}
                placeholder="Número de documento"
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Nombre Completo</FormLabel>
              <Input
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Nombre o razón social"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Dirección</FormLabel>
              <Input
                value={formData.address}
                onChange={(e) => handleChange('address', e.target.value)}
                placeholder="Dirección del cliente"
              />
            </FormControl>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <FormControl>
                <FormLabel>Ciudad</FormLabel>
                <Input
                  value={formData.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                  placeholder="Ciudad del cliente"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Departamento/Estado</FormLabel>
                <Input
                  value={formData.state}
                  onChange={(e) => handleChange('state', e.target.value)}
                  placeholder="Departamento o estado del cliente"
                />
              </FormControl>
            </SimpleGrid>

            <FormControl>
              <FormLabel>País</FormLabel>
              <Select
                value={formData.country}
                onChange={(e) => handleChange('country', e.target.value)}
              >
                <option value="URUGUAY">Uruguay</option>
                <option value="ARGENTINA">Argentina</option>
                <option value="BRASIL">Brasil</option>
                <option value="ESTADOS UNIDOS">Estados Unidos</option>
                <option value="ESPAÑA">España</option>
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel>Email (Opcional)</FormLabel>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="cliente@email.com"
              />
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleClose}>
            Cancelar
          </Button>
          <Button 
            colorScheme="gray" 
            onClick={handleSubmit}
            isLoading={isLoading}
            loadingText="Creando..."
          >
            Crear Cliente
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
